"use client"

import { Button } from "@/components/ui/button"
import { Share2, Link as LinkIcon, Copy, Check } from "lucide-react"
import { TwitterIcon as Twitter, FacebookIcon as Facebook, LinkedinIcon as Linkedin } from "./brand-icons"
import { useEffect, useState } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface BlogShareButtonsProps {
  url?: string
  title?: string
  description?: string
  variant?: 'default' | 'compact' | 'glass'
}

export function BlogShareButtons({ 
  url, 
  title = "Lawn Care Tips & Resources",
  description = "Expert lawn care tips and resources",
  variant = 'default'
}: BlogShareButtonsProps) {
  const [copied, setCopied] = useState(false)
  // Seed with the prop (or empty) to match server render; resolve from
  // window.location.href after mount when no explicit url was passed.
  const [shareUrl, setShareUrl] = useState(url || '')
  useEffect(() => {
    setShareUrl(url || window.location.href)
  }, [url])
  const shareTitle = encodeURIComponent(title)
  const shareDescription = encodeURIComponent(description)
  const shareUrlEncoded = encodeURIComponent(shareUrl)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrlEncoded}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`,
  }

  // Frosted glass circles for the full-bleed photo hero (Wild Violet v2):
  // white icons on translucent circles read clearly over the cover image.
  if (variant === 'glass') {
    const circle =
      "grid h-9 w-9 place-items-center rounded-full border border-white/25 bg-white/15 text-white backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-white/25"
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Share on X"
          onClick={() => window.open(shareLinks.twitter, '_blank', 'noopener,noreferrer')}
          className={circle}
        >
          <Twitter className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Share on Facebook"
          onClick={() => window.open(shareLinks.facebook, '_blank', 'noopener,noreferrer')}
          className={circle}
        >
          <Facebook className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={copied ? 'Link copied' : 'Copy link'}
          onClick={handleCopy}
          className={circle}
        >
          {copied ? <Check className="h-4 w-4 text-green-300" /> : <LinkIcon className="h-4 w-4" />}
        </button>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            aria-label="Share this page"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onClick={() => window.open(shareLinks.twitter, '_blank', 'noopener,noreferrer')}
            className="cursor-pointer"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Twitter
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => window.open(shareLinks.facebook, '_blank', 'noopener,noreferrer')}
            className="cursor-pointer"
          >
            <Facebook className="w-4 h-4 mr-2" />
            Facebook
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => window.open(shareLinks.linkedin, '_blank', 'noopener,noreferrer')}
            className="cursor-pointer"
          >
            <Linkedin className="w-4 h-4 mr-2" />
            LinkedIn
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleCopy}
            className="cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <LinkIcon className="w-4 h-4 mr-2" />
                Copy Link
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <span className="text-sm font-medium text-gray-700 mr-2">Share:</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(shareLinks.twitter, '_blank', 'noopener,noreferrer')}
        className="gap-2"
        aria-label="Share on Twitter"
      >
        <Twitter className="w-4 h-4" />
        Twitter
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(shareLinks.facebook, '_blank', 'noopener,noreferrer')}
        className="gap-2"
        aria-label="Share on Facebook"
      >
        <Facebook className="w-4 h-4" />
        Facebook
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(shareLinks.linkedin, '_blank', 'noopener,noreferrer')}
        className="gap-2"
        aria-label="Share on LinkedIn"
      >
        <Linkedin className="w-4 h-4" />
        LinkedIn
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className="gap-2"
        aria-label="Copy link"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-green-600" />
            Copied!
          </>
        ) : (
          <>
            <LinkIcon className="w-4 h-4" />
            Copy Link
          </>
        )}
      </Button>
    </div>
  )
}
